/* eslint-disable no-template-curly-in-string */
import React, { useEffect } from 'react'
import { BrowserRouter as Router, Switch, Redirect } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import LoadingWithText from './components/LoadingWithText/LoadingWithText'

import { withAITracking } from '@microsoft/applicationinsights-react-js'
import { reactPlugin } from './AppInsights'

import UserStateWrapper from './UserStateWrapper'

import PrivateRoute from './components/PrivateRoute'
import HighchartsInit from './highcharts/HighchartsInit'

import Layout from './layouts/Layout'
import IpsaLayout from './layouts/IpsaLayout'
import LayerLayout from './layouts/LayerLayout'

import Dashboard from './pages/Dashboard'
import Ipsa from './pages/Ipsa'
import Account from './pages/Account'
import Insights from './pages/Insights'
import Tools from './pages/Tools'
import WhatsNew from './pages/WhatsNew'
import Training from './pages/Training'
import Support from './pages/Support'

import useCookie from './hooks/useCookie'
import { UserAuthenticationSingleton } from './auth/UserAuthenticationSingleton'

HighchartsInit()

const AnyPlanRequired = ['IpsaBeta', 'IpsaMonthly', 'IpsaYearly', 'IpsaFreeTrial']
const NoPlanRequired = []

const App = () => {
    const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0()

    const [regCookieValue, setRegCookieValue] = useCookie('signUpPlanId')
    const [affiliateCookieValue, setAffiliateCookieValue] = useCookie('affId')

    const [accessTokenSet, setAccessTokenSet] = React.useState(false)

    useEffect(() => {
        if (isLoading || isAuthenticated) {
            if (isAuthenticated) {
                getAccessTokenSilently().then((access_token) => {
                    UserAuthenticationSingleton.setAccessToken(access_token)
                    setAccessTokenSet(true)
                })
            }

            return
        }

        const search = window.location.search
        const planIdQueryParam = new URLSearchParams(search).get('planid')
        const affiliateQueryParam = new URLSearchParams(search).get('affid')
        const signUpQueryParam = new URLSearchParams(search).get('signup')

        if (planIdQueryParam) {
            setRegCookieValue(planIdQueryParam, 15)
        }

        if (affiliateQueryParam) {
            setAffiliateCookieValue(affiliateQueryParam, 15)
        }

        const fn = async () => {
            const screen_hint = signUpQueryParam === 'signup' ? 'signUp' : ''
            loginWithRedirect({
                appState: {
                    targetUrl: window.location.pathname + window.location.search,
                },
                screen_hint,
            })
        }

        fn()
    }, [isLoading, isAuthenticated, loginWithRedirect])

    if (!isAuthenticated) {
        return <LoadingWithText text="Loading User" />
    }

    if (!accessTokenSet) {
        return <LoadingWithText text="Loading User Access Tokens" />
    }

    return (
        <UserStateWrapper>
            <Router>
                <Switch>
                    <Redirect from="/" to={{ ...window.location, pathname: '/dashboard' }} exact />

                    <Redirect
                        from="/freetrial"
                        to={{
                            ...window.location,
                            pathname: '/account/subscription/freetrial',
                        }}
                        exact
                    />

                    <PrivateRoute
                        path="/dashboard"
                        component={Dashboard}
                        layout={Layout}
                        subscriptionLevelsRequired={AnyPlanRequired}
                    />

                    <PrivateRoute
                        path="/clients"
                        component={Ipsa}
                        layout={IpsaLayout}
                        subscriptionLevelsRequired={AnyPlanRequired}
                    />
                    <PrivateRoute
                        path="/training"
                        component={Training}
                        layout={LayerLayout}
                        subscriptionLevelsRequired={AnyPlanRequired}
                    />
                    <PrivateRoute
                        path="/insights"
                        component={Insights}
                        layout={LayerLayout}
                        subscriptionLevelsRequired={AnyPlanRequired}
                    />
                    <PrivateRoute
                        path="/tools"
                        component={Tools}
                        layout={LayerLayout}
                        subscriptionLevelsRequired={AnyPlanRequired}
                    />

                    <PrivateRoute
                        path="/whatsnew"
                        component={WhatsNew}
                        layout={Layout}
                        subscriptionLevelsRequired={NoPlanRequired}
                    />
                    <PrivateRoute
                        path="/account"
                        component={Account}
                        layout={Layout}
                        subscriptionLevelsRequired={NoPlanRequired}
                    />
                    <PrivateRoute
                        path="/support"
                        component={Support}
                        layout={Layout}
                        subscriptionLevelsRequired={NoPlanRequired}
                    />
                </Switch>
            </Router>
        </UserStateWrapper>
    )
}

export default withAITracking(reactPlugin, App)
